import { AddCircleOutlined } from "@mui/icons-material";
import TuneIcon from "@mui/icons-material/Tune";
import { Box } from "@mui/material";
import { type FC, type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { SystemPageHeader } from "../../../components/UIToolkit/molecules/SystemPageHeader";
import type { DialogCheckedState } from "../../../components/UIToolkit/Popups/Dialog";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import type { PbjBreakRule } from "../../../utils/models";
import { prefersReducedMotion } from "../../../utils/reducedMotion";
import { usePbjBreakRules, useSavePbjBreakRules } from "../hooks/usePbjBreakRules";
import { usePbjJobMapping } from "../hooks/usePbjJobs";
import { PbjBreakRulesTable } from "./PbjBreakRulesTable";

// Stable serialization of the editable fields so dirty-detection ignores server drift (timestamps/ids
// are still compared via id, but ordering of multi-value lists is normalized).
const serializeRules = (rules: PbjBreakRule[]): string =>
	JSON.stringify(
		rules.map((r) => ({
			name: r.name,
			rawJobs: [...(r.rawJobs ?? [])].sort(),
			employmentTypes: [...(r.employmentTypes ?? [])].sort(),
			breakLimit: r.breakLimit,
			breakStart: r.breakStart ?? null,
			type: r.type,
			breakTime: r.breakTime,
		})),
	);

/**
 * "Breaks" tab — view/edit/remove the current department's PBJ break rules (the programmatic
 * meal-break deductions PBJ Staffing Hours applies, stored in the dedicated pbj_break_rules table).
 * Edits buffer locally; Save opens a confirmation that can apply the rule set to every department in
 * the network (department.hospital) or this dept only. Scheduling break rules are a separate table.
 */
type Props = {
	// Wraps a "Period & facility" header button in the page-level period/facility popover. The date
	// controls are inert here (Breaks is dept-config, not date-scoped), but facility + Calculate apply.
	renderChangePeriod: (trigger: ReactElement) => ReactNode;
};

export const BreaksTab: FC<Props> = ({ renderChangePeriod }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	const deptId = chosenDepartment.department_id;

	const rulesQuery = usePbjBreakRules(deptId);
	const mappingQuery = usePbjJobMapping(deptId);
	const saveMutation = useSavePbjBreakRules(deptId);

	const [draft, setDraft] = useState<PbjBreakRule[]>([]);
	const [confirmOpen, setConfirmOpen] = useState(false);
	// Id of the just-added rule — prepended to the top and spotlighted so it's obviously the new one.
	const [newRuleId, setNewRuleId] = useState<string | null>(null);

	const serverRules = useMemo(() => rulesQuery.data ?? [], [rulesQuery.data]);

	// Re-seed the draft whenever the loaded rules change (dept switch + post-save refresh).
	useEffect(() => {
		setDraft(serverRules);
	}, [serverRules]);

	useEffect(() => {
		track("pbj_breaks_tab_viewed", { department_id: deptId });
	}, [deptId, track]);

	// The Eligible Roles picker source: the dept's known HRIS role strings (same list as the Jobs tab).
	const roleStringOptions = useMemo(() => (mappingQuery.data ?? []).map((m) => m.roleString), [mappingQuery.data]);

	const isDirty = useMemo(() => serializeRules(draft) !== serializeRules(serverRules), [draft, serverRules]);

	const addRow = useCallback(() => {
		const id = uuidv4();
		// Prepend so the new rule lands at the top (not buried below existing ones).
		setDraft((prev) => [
			{
				id,
				name: "",
				employmentTypes: [],
				rawJobs: [],
				breakLimit: 0,
				type: "addition",
				breakTime: 30,
				departmentId: deptId,
			} as PbjBreakRule,
			...prev,
		]);
		setNewRuleId(id);
	}, [deptId]);

	// Bring the freshly-added rule (now first) into view; the card flags itself "new" via a one-shot
	// highlight animation. Runs after the prepend commits, so the top card is in the DOM.
	useEffect(() => {
		if (!newRuleId) return;
		document.querySelector('[data-testid="pbj-breaks-rule-0"]')?.scrollIntoView({
			behavior: prefersReducedMotion() ? "auto" : "smooth",
			block: "nearest",
		});
	}, [newRuleId]);

	const handleConfirmSave = useCallback(
		(checkedState?: DialogCheckedState[]) => {
			const applyToNetwork = checkedState?.[0]?.checked ?? false;
			// Stamp this dept on every row (the BE re-asserts department_id anyway, but keep the payload honest).
			const rules = draft.map((r) => ({ ...r, departmentId: deptId }));
			saveMutation.mutate({ departmentId: deptId, applyToNetwork, rules }, { onSuccess: () => setConfirmOpen(false) });
		},
		[draft, deptId, saveMutation],
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }} data-testid="pbj-breaks-tab">
			<SystemPageHeader
				dataTestId="pbj-breaks-header"
				eyebrow={t("pbj_model.tabs.breaks")}
				title={t("pbj_model.breaks.title")}
				subtitle={t("pbj_model.breaks.subtitle")}
				actions={
					<>
						{renderChangePeriod(
							<SystemButton buttonVariant="secondary" size="small" leadingIcon={<TuneIcon sx={{ fontSize: 16 }} />} noFullWidthMobile>
								{t("pbj_model.hero.change_period")}
							</SystemButton>,
						)}
						<SystemButton
							buttonVariant="secondary"
							size="small"
							onClick={addRow}
							leadingIcon={<AddCircleOutlined sx={{ fontSize: 18 }} />}
							noFullWidthMobile
							data-testid="pbj-breaks-add-button"
						>
							{t("pbj_model.breaks.add")}
						</SystemButton>
						<SystemButton
							buttonVariant={isDirty ? "primary" : "secondary"}
							size="small"
							onClick={() => setConfirmOpen(true)}
							disabled={!isDirty}
							loading={saveMutation.isPending}
							noFullWidthMobile
							data-testid="pbj-breaks-save-button"
						>
							{t("pbj_model.breaks.save")}
						</SystemButton>
					</>
				}
			/>

			{/* Cards flow in the page's scroll (like the Audit/Submission tabs) — no fixed-height inner
			    scroller. Depth comes from the faint brand wash on the content canvas (set in PbjModelPage
			    for every tab) that the shadowed cards lift off of. */}
			<PbjBreakRulesTable rules={draft} onChange={setDraft} roleStringOptions={roleStringOptions} highlightRuleId={newRuleId} />

			<SystemDialog
				open={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				title={t("pbj_model.breaks.save_confirm.title")}
				body={t("pbj_model.breaks.save_confirm.body")}
				confirm={t("pbj_model.breaks.save_confirm.apply")}
				onConfirm={handleConfirmSave}
				confirmLoading={saveMutation.isPending}
				checkboxes={[{ label: t("pbj_model.breaks.save_confirm.network"), required: false, defaultValue: false }]}
				dataTestId="pbj-breaks-save-confirm"
			/>
		</Box>
	);
};
